import React from 'react';
import { string } from 'prop-types';
import { Helmet } from 'react-helmet-async';

/**
 * Head metadata for pages that are routed directly rather than through <Page>.
 *
 * Most containers render inside <Page>, which emits the title, description,
 * canonical and social tags for them. A handful of the marketing pages are
 * routed straight to their own component and never touch it, so they emit
 * nothing.
 *
 * That was invisible while public/index.html hardcoded a set of homepage tags
 * into the shared head: those pages inherited the homepage's description and
 * Open Graph tags, and — the reason the tags had to go — the homepage's
 * canonical, which told crawlers to index the homepage in their place.
 *
 * Removing the shared tags fixed the canonical for every page and left these
 * ones with no description or social tags at all. This is what fills that gap:
 * the same shape <Page> produces, for routes that do not use it.
 *
 * Prefer <Page> for anything new. This exists for the pages that already
 * bypass it, not as an alternative to it.
 */
const ROOT_URL = 'https://skipthedealerships.ai';

// Shared with the sitewide Open Graph image. It is 1200x630, the aspect ratio
// Facebook, LinkedIn and Twitter's summary_large_image card all expect.
const DEFAULT_IMAGE = `${ROOT_URL}/static/images/dealer-portal-preview.png`;
const DEFAULT_IMAGE_WIDTH = '1200';
const DEFAULT_IMAGE_HEIGHT = '630';

const PageMeta = props => {
  const {
    title,
    description,
    path,
    image = DEFAULT_IMAGE,
    imageWidth = DEFAULT_IMAGE_WIDTH,
    imageHeight = DEFAULT_IMAGE_HEIGHT,
    openGraphType = 'website',
    locale = 'en_CA',
  } = props;

  // Self-referencing: the page declares itself canonical. Exactly one of these
  // may exist per page — a second one, whatever it points at, sends crawlers to
  // whichever they read first.
  const url = `${ROOT_URL}${path}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={url} />

      <meta property="og:type" content={openGraphType} />
      <meta property="og:site_name" content="SkipTheDealerships.ai" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:locale" content={locale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

PageMeta.propTypes = {
  title: string.isRequired,
  description: string.isRequired,
  /** Absolute path beginning with a slash, e.g. "/faq". */
  path: string.isRequired,
  image: string,
  imageWidth: string,
  imageHeight: string,
  openGraphType: string,
  /** Open Graph locale (e.g., "en_CA", "fr_CA"). Defaults to "en_CA". */
  locale: string,
};

export default PageMeta;
